import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { formsApi } from '../api/client';
import type {
  FormComponent as FormComponentType,
  FormSplitComponent as FormSplitComponentType,
} from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { usePageContext } from '../contexts/PageContext';
import { getThemeStyles } from '../styles/themes';

export type FormCoreCompatible =
  | Pick<FormComponentType, 'id' | 'data' | 'type'>
  | Pick<FormSplitComponentType, 'id' | 'data' | 'type'>;

export interface FormCoreProps {
  component: FormCoreCompatible;
  /** Optional wrapper class for the form root (e.g. full width in split layout) */
  className?: string;
}

interface FormState {
  [key: string]: string | string[];
}

interface FormErrors {
  [key: string]: string;
}

/** Normalizes options from API/editor — converts {value, label} objects to plain strings */
export const normalizeOptions = (options: unknown[] | undefined): string[] => {
  if (!options) return [];
  return options
    .map(opt => {
      if (typeof opt === 'string') return opt;
      if (typeof opt === 'object' && opt !== null) {
        const objOpt = opt as Record<string, unknown>;
        return String(objOpt.label || objOpt.value || objOpt.text || '');
      }
      return String(opt);
    })
    .filter(Boolean);
};

export const FormCore: React.FC<FormCoreProps> = ({ component, className = '' }) => {
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const { pageId } = usePageContext();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const rawData = component.data as Record<string, unknown>;
  const rawFields = (rawData.fields || []) as Array<Record<string, unknown>>;
  const normalizedFields = rawFields.map((field, index) => ({
    ...field,
    options: normalizeOptions(field.options as unknown[] | undefined),
    order: (field.order as number) ?? index,
  }));

  const data = {
    ...component.data,
    fields: normalizedFields,
  } as typeof component.data;

  const [formState, setFormState] = useState<FormState>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const textColor =
    (data as any).customColors?.text ||
    data.styling?.textColor ||
    themeStyles.form?.labelColor ||
    '#374151';
  const inputBgColor =
    (data as any).customColors?.inputBackground || themeStyles.form?.inputBg || '#ffffff';
  const inputBorderColor =
    (data as any).customColors?.inputBorder || themeStyles.form?.inputBorder || '#d1d5db';
  const inputTextColor =
    (data as any).customColors?.inputText || themeStyles.form?.inputText || '#111827';
  const labelColor = (data as any).customColors?.label || textColor;
  const buttonBgColor =
    (data as any).customColors?.buttonBackground || themeStyles.form?.buttonBg || '#3B82F6';
  const buttonTextColor =
    (data as any).customColors?.buttonText || themeStyles.form?.buttonText || '#ffffff';
  const buttonHoverColor = themeStyles.form?.buttonHover || '#2563EB';
  const buttonStyle = data.submitButton?.style || 'primary';

  const getSpacingClasses = () => {
    switch (data.styling.spacing) {
      case 'compact':
        return 'space-y-3';
      case 'spacious':
        return 'space-y-8';
      default:
        return 'space-y-6';
    }
  };

  const getLayoutClasses = () => {
    return data.styling.layout === 'two-column'
      ? 'grid grid-cols-1 md:grid-cols-2 gap-6'
      : 'space-y-6';
  };

  const validateField = (field: any, value: string | string[]): string | null => {
    const stringValue = Array.isArray(value) ? value.join(', ') : value;

    if (field.required && (!stringValue || stringValue.trim() === '')) {
      return `${field.label} is required`;
    }

    if (!stringValue || stringValue.trim() === '') {
      return null;
    }

    if (field.type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(stringValue)) {
        return `${field.label} must be a valid email address`;
      }
    }

    if (field.validation) {
      const { minLength, maxLength, pattern } = field.validation;

      if (minLength && stringValue.length < minLength) {
        return `${field.label} must be at least ${minLength} characters`;
      }

      if (maxLength && stringValue.length > maxLength) {
        return `${field.label} must be no more than ${maxLength} characters`;
      }

      if (pattern) {
        try {
          const regex = new RegExp(pattern);
          if (!regex.test(stringValue)) {
            return `${field.label} format is invalid`;
          }
        } catch {
          // Invalid regex pattern - skip validation
        }
      }
    }

    return null;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    data.fields.forEach(field => {
      const error = validateField(field, formState[field.identifier] || '');
      if (error) {
        newErrors[field.identifier] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!pageId) {
      console.error('Form submission error: pageId not available in context');
      setSubmitStatus('error');
      setSubmitMessage('Unable to submit form. Please try again later.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const result = await formsApi.submitForm(pageId, component.id, formState);

      const redirectTarget =
        result.redirectUrl ||
        (data.emailSettings?.redirectOnSuccess && data.emailSettings.redirectUrl?.trim()
          ? data.emailSettings.redirectUrl.trim()
          : '');
      if (redirectTarget) {
        window.location.href = redirectTarget;
        return;
      }

      setSubmitStatus('success');
      setSubmitMessage(
        result?.message || data.emailSettings.successMessage || 'Thank you for your submission!'
      );
      setFormState({});
      setErrors({});
      try {
        document.dispatchEvent(
          new CustomEvent('cp:form-success', {
            bubbles: true,
            detail: { pageId, componentId: component.id },
          })
        );
      } catch {
        /* ignore */
      }
    } catch (error: unknown) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      const msg = error instanceof Error ? error.message : '';
      setSubmitMessage(
        msg || data.emailSettings.errorMessage || 'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (identifier: string, value: string | string[]) => {
    setFormState(prev => ({ ...prev, [identifier]: value }));

    if (errors[identifier]) {
      setErrors(prev => ({ ...prev, [identifier]: '' }));
    }
  };

  const renderField = (field: any) => {
    const fieldValue = formState[field.identifier] || '';
    const fieldError = errors[field.identifier];
    const fieldId = `field-${field.identifier}`;

    const baseInputClasses =
      'w-full px-4 py-3 border rounded-lg transition-colors focus:ring-2 focus:outline-none';

    const baseInputStyles: React.CSSProperties = fieldError
      ? {
          backgroundColor: '#FEF2F2',
          borderColor: '#FCA5A5',
          color: '#111827',
        }
      : {
          backgroundColor: inputBgColor,
          borderColor: inputBorderColor,
          color: inputTextColor,
        };

    switch (field.type) {
      case 'textarea':
        return (
          <div key={field.identifier} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="block text-sm font-medium"
              style={{ color: labelColor, ...bodyFontStyle }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              id={fieldId}
              name={field.identifier}
              value={fieldValue as string}
              onChange={e => handleInputChange(field.identifier, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              rows={4}
              className={baseInputClasses}
              style={baseInputStyles}
            />
            {fieldError && (
              <p className="text-sm text-red-600 flex items-center space-x-1">
                <AlertCircle size={16} />
                <span>{fieldError}</span>
              </p>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={field.identifier} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="block text-sm font-medium"
              style={{ color: labelColor, ...bodyFontStyle }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              id={fieldId}
              name={field.identifier}
              value={fieldValue as string}
              onChange={e => handleInputChange(field.identifier, e.target.value)}
              required={field.required}
              className={baseInputClasses}
              style={baseInputStyles}
            >
              <option value="">Select an option</option>
              {field.options?.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {fieldError && (
              <p className="text-sm text-red-600 flex items-center space-x-1">
                <AlertCircle size={16} />
                <span>{fieldError}</span>
              </p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.identifier} className="space-y-2">
            <fieldset>
              <legend
                className="block text-sm font-medium"
                style={{ color: labelColor, ...bodyFontStyle }}
              >
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </legend>
              <div className="mt-2 space-y-2">
                {field.options?.map((option: string) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="checkbox"
                      name={field.identifier}
                      value={option}
                      checked={(fieldValue as string[])?.includes(option) || false}
                      onChange={e => {
                        const currentValues = (fieldValue as string[]) || [];
                        const newValues = e.target.checked
                          ? [...currentValues, option]
                          : currentValues.filter(v => v !== option);
                        handleInputChange(field.identifier, newValues);
                      }}
                      className="rounded border focus:ring-2 focus:outline-none"
                      style={{
                        borderColor: inputBorderColor,
                        accentColor: buttonBgColor,
                      }}
                    />
                    <span className="ml-2 text-sm" style={{ color: labelColor }}>
                      {option}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            {fieldError && (
              <p className="text-sm text-red-600 flex items-center space-x-1">
                <AlertCircle size={16} />
                <span>{fieldError}</span>
              </p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div key={field.identifier} className="space-y-2">
            <fieldset>
              <legend
                className="block text-sm font-medium"
                style={{ color: labelColor, ...bodyFontStyle }}
              >
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </legend>
              <div className="mt-2 space-y-2">
                {field.options?.map((option: string) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={field.identifier}
                      value={option}
                      checked={fieldValue === option}
                      onChange={e => handleInputChange(field.identifier, e.target.value)}
                      className="border focus:ring-2 focus:outline-none"
                      style={{
                        borderColor: inputBorderColor,
                        accentColor: buttonBgColor,
                      }}
                    />
                    <span className="ml-2 text-sm" style={{ color: labelColor }}>
                      {option}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            {fieldError && (
              <p className="text-sm text-red-600 flex items-center space-x-1">
                <AlertCircle size={16} />
                <span>{fieldError}</span>
              </p>
            )}
          </div>
        );

      default:
        return (
          <div key={field.identifier} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="block text-sm font-medium"
              style={{ color: labelColor, ...bodyFontStyle }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              id={fieldId}
              type={field.type}
              name={field.identifier}
              value={fieldValue as string}
              onChange={e => handleInputChange(field.identifier, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              className={baseInputClasses}
              style={baseInputStyles}
            />
            {fieldError && (
              <p className="text-sm text-red-600 flex items-center space-x-1">
                <AlertCircle size={16} />
                <span>{fieldError}</span>
              </p>
            )}
          </div>
        );
    }
  };

  const sortedFields = [...data.fields].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <>
      <style>{`
        .form-submit-button-${component.id}:hover {
          ${
            buttonStyle === 'outline'
              ? `background-color: ${buttonBgColor} !important; color: ${buttonTextColor} !important;`
              : buttonStyle === 'secondary'
                ? `background-color: ${buttonBgColor}33 !important;`
                : `background-color: ${buttonHoverColor} !important;`
          }
        }
      `}</style>
      <form
        onSubmit={handleSubmit}
        className={`${getSpacingClasses()} ${className}`.trim()}
        aria-label={data.title}
      >
        {submitStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="text-green-600" size={20} />
            <p className="text-green-800">{submitMessage}</p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
            <AlertCircle className="text-red-600" size={20} />
            <p className="text-red-800">{submitMessage}</p>
          </div>
        )}

        <div className={getLayoutClasses()}>{sortedFields.map(renderField)}</div>

        <div className="pt-6">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`form-submit-button-${component.id} w-full px-6 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            style={
              buttonStyle === 'outline'
                ? {
                    backgroundColor: 'transparent',
                    color: buttonBgColor,
                    border: `2px solid ${buttonBgColor}`,
                  }
                : buttonStyle === 'secondary'
                  ? {
                      backgroundColor: `${buttonBgColor}1A`,
                      color: buttonBgColor,
                    }
                  : {
                      backgroundColor: buttonBgColor,
                      color: buttonTextColor,
                    }
            }
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center space-x-2">
                <Loader2 size={20} className="animate-spin" />
                <span>Submitting...</span>
              </span>
            ) : (
              data.submitButton.text
            )}
          </button>
        </div>
      </form>
    </>
  );
};
